import React from 'react';
import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Paper,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Box,
  Button
} from '@mui/material';

import NoteIcon from '@mui/icons-material/Note';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../redux/actions';

import '../styles/lookup.scss';
import 'materialize-css';

function toDateString(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseMdYyyy(dateStr) {
  const [m, d, y] = dateStr.split('/').map(Number);
  return new Date(y, m - 1, d).getTime();
}

function ColumnSparkline({ values, width = 64, height = 28 }) {
  const numeric = values.map(v => Number(v)).filter(n => !Number.isNaN(n));
  if (numeric.length < 2) return null;
  const min = Math.min(...numeric);
  const max = Math.max(...numeric);
  const range = max - min || 1;
  const padding = 2;
  const w = width - padding * 2;
  const h = height - padding * 2;
  const step = w / (numeric.length - 1);
  const points = numeric.map((val, i) => {
    const x = padding + i * step;
    const y = padding + h - ((val - min) / range) * h;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} className="system-review-sparkline" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function SystemReviewModal(props) {
  const [hoveredColIndex, setHoveredColIndex] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return toDateString(d);
  });
  const [endDate, setEndDate] = useState(() => toDateString(new Date()));
  const [showNoteText, setShowNoteText] = useState(null);

  const handleFetchSystemReview = (update) => {
    console.log('handleFetchSystemReview', update);
    let systemId = update.component || props.system_review?.component;
    let sDate = update.start_date || startDate;
    let eDate = update.end_date || endDate;
    if (!systemId || !sDate || !eDate) return;
    console.log('fetching system review for system', systemId, 'from', sDate, 'to', eDate);
    props.fetchSystemReview(props.authToken, systemId, sDate, eDate);
  }

  const cellColor = (result) => {
    // Helper to determine how far out of range
    const shadeYellow = '#fff3cd';
    const shadeRed = '#f8d7da';
    const shadeGreen = '#d4edda';
    
    if (result.max !== null && result.min !== null) {
      if (result.result >= result.min && result.result <= result.max) {
        return { backgroundColor: shadeGreen };
      } else {
        let distance = Math.max(result.min - result.result, result.result - result.max);
        if (distance > (result.max - result.min) * 0.1) {
          return { backgroundColor: shadeRed };
        } else {
          return { backgroundColor: shadeYellow };
        }
      }
    } else if (result.min !== null && result.result < result.min) {
      return { backgroundColor: shadeRed };
    } else if (result.max !== null && result.result > result.max) {
      return { backgroundColor: shadeRed };
    } else {
      return { backgroundColor: shadeGreen };
    }
  }

  const formatLimitsText = (min, max) => {
    if (min && max) {
      return <span className="limits-text">{` (${min}–${max})`}</span>
    } else if (min) {
      return <span className="limits-text">{` (≥${min})`}</span>
    } else if (max) {
      return <span className="limits-text">{` (≤${max})`}</span>
    } else {
      return null;
    }
  }

  const renderReportNote = (date) => {
    let note = props.system_review.notes?.find(note => note.date === date)?.note;
    if (note) {
      return [
        <NoteIcon className="note-icon"/>, 
        <span className={`note-text ${showNoteText === `report-note-${date}` ? 'show' : ''}`} key={`report-note-${date}`}>{note}</span>
      ]
    }
    return null;
  }

  const sortedDates = props.system_review
    ? Object.keys(props.system_review.rows).sort((a, b) => parseMdYyyy(a) - parseMdYyyy(b))
    : [];

  const getColumnValues = (colIndex) =>
    sortedDates
      .map(date => props.system_review.rows[date][colIndex]?.result)
      .filter(v => v != null);

  const renderChart = (colIndex) => {
    const values = getColumnValues(colIndex);
    return <ColumnSparkline values={values} />;
  }

  const buildExportRows = () => {
    const headers = ['Date', ...props.system_review.test_fields];
    const rows = sortedDates.map(date => {
      const rowData = props.system_review.rows[date];
      return [
        date,
        ...rowData.map((result) => {
          return `${result.result ?? ''}`;
        })
      ];
    });
    return [headers, ...rows];
  };

  const handleExportPdf = () => {
    if (!props.system_review) return;
    const [headers, ...rows] = buildExportRows();
    const doc = new jsPDF({ orientation: 'landscape' });
    const systemName = props.systems.find(s => s.id === props.system_review.component)?.name || 'System Review';
    doc.setFontSize(14);
    doc.text(`System Review: ${systemName}`, 14, 12);
    doc.setFontSize(10);
    doc.text(`${startDate} to ${endDate}`, 14, 18);

    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 22,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [66, 139, 202] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 14 },
    });

    const fileName = `system-review-${systemName}-${startDate}-${endDate}.pdf`;
    doc.save(fileName);
  };

  const handleExportExcel = () => {
    if (!props.system_review) return;
    const data = buildExportRows();
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    const systemName = (props.systems.find(s => s.id === props.system_review.component)?.name || 'System Review').replace(/[/\\*?:\[\]]/g, ' ');
    XLSX.utils.book_append_sheet(wb, ws, systemName.slice(0, 31));
    const fileName = `system-review-${systemName}-${startDate}-${endDate}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className="modal-backdrop" onClick={props.close}>
      <div className="modal system-review-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={props.close}><CloseIcon /></button> 
        <div className="modal-content">
          <center><h4>System Review</h4></center>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, minWidth: 320 }}>
            <Box className="system-review-form-row" sx={{ display: 'flex', flex: 1,flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
              <FormControl size="small" variant="outlined" sx={{ minWidth: 250 }}>
                <InputLabel id="system-review-system-label">System</InputLabel>
                <Select
                  className="system-review-component-select"
                  labelId="system-review-system-label"
                  label="System"
                  placeholder="Select a system"
                  value={props.system_review?.component}
                  onChange={e => handleFetchSystemReview({ component: e.target.value })}
                >
                  {props.systems.map(system => (
                    <MenuItem key={system.id} value={system.id}>
                      {system.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box className="system-review-date-input-container">
                <TextField
                  className="system-review-date-input"
                  label="Start date"
                  type="date"
                  size="small"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  className="system-review-date-input"
                  label="End date"
                  type="date"
                  size="small"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>

            {props.system_review && (
              <TableContainer component={Paper} className="system-review-table-container">
                <Table
                  className="system-review-table"
                  size="small"
                  onMouseLeave={() => setHoveredColIndex(null)}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell className={`report-date-column-header ${hoveredColIndex === 0 ? 'header-cell-hover' : ''}`}>Date</TableCell>
                      {props.system_review.test_fields.map((test_field, colIndex) => (
                        <TableCell key={test_field} className={hoveredColIndex === colIndex + 1 ? 'header-cell-hover' : ''}>
                          {test_field}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedDates.map(date => (
                      <TableRow key={date}>
                        <TableCell
                          onMouseEnter={() => {
                            setHoveredColIndex(0);
                            setShowNoteText(`report-note-${date}`);
                          }}
                          onMouseLeave={() => setShowNoteText(null)}
                          onClick={() => window.open(props.system_review.rows[date][0].report_url, '_blank')}
                        >
                          {date}
                          {renderReportNote(date)}
                        </TableCell>
                        {props.system_review.rows[date].map((result, index) => (
                          <TableCell
                            key={index}
                            sx={cellColor(result)}
                            onMouseEnter={() => setHoveredColIndex(index + 1)}
                            onClick={() => window.open(result.report_url, '_blank')}
                          >
                            <div className="result-cell">
                              <span className="result-value"><b>{result.result}</b></span>
                              <span className="limits-text">{formatLimitsText(result.min, result.max)}</span>
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    <TableRow className="system-review-chart-row">
                      <TableCell className="system-review-chart-label">Result Trend</TableCell>
                      {props.system_review.test_fields.map((_, colIndex) => (
                        <TableCell key={colIndex} className="system-review-chart-cell">
                          {renderChart(colIndex)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {props.system_review && (
              <Box className="system-review-export-buttons" sx={{ display: 'flex', flex: 1, justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TableChartIcon />}
                  onClick={handleExportExcel}
                >
                  Export to Excel
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleExportPdf}
                >
                  Export PDF
                </Button>
              </Box>
            )}

          </Box>
        </div>
      </div>
    </div>
  );
}

function mapStateToProps(state) {
  return {
    authToken: state.authToken,
    systems: state.info?.report_components || [],
    system_review: state.info?.system_review,
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(SystemReviewModal);
